import React, { useState, useMemo, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowLeft, Brain, Cpu, Zap, Heart, MessageCircle, BookOpen, Eye, Sparkles, ArrowRight, Shield, FlaskConical, Quote, FileText, Users, Activity, Moon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import WaitlistHeader from './WaitlistHeader';
import './BrainComparison.css';

interface BrainComparisonProps {
    onBack?: () => void;
    onOpenAuthModal?: (mode: 'login' | 'signup') => void;
}

/* ─── Brain region data ─── */
const BRAIN_REGIONS = [
    { id: 'prefrontal', cx: 155, cy: 120, color: 'var(--accent)', replicaName: 'Decision Engine', brainName: 'Prefrontal Cortex', desc: 'Planning, decision-making, and complex reasoning', coverage: 78, scienceNote: 'Transformer attention layers mirror selective executive attention' },
    { id: 'hippocampus', cx: 280, cy: 200, color: '#34D399', replicaName: 'Long-Term Memory', brainName: 'Hippocampus', desc: 'Memory formation, storage and retrieval', coverage: 85, scienceNote: 'Vector embeddings replicate associative memory retrieval' },
    { id: 'amygdala', cx: 245, cy: 225, color: '#F472B6', replicaName: 'Emotional Intelligence', brainName: 'Amygdala', desc: 'Emotional processing and response', coverage: 62, scienceNote: 'Sentiment models approximate affective computing, not felt emotion' },
    { id: 'broca', cx: 175, cy: 170, color: '#FBBF24', replicaName: 'Language Generation', brainName: "Broca's Area", desc: 'Speech production and language processing', coverage: 91, scienceNote: 'Autoregressive generation parallels sequential speech encoding' },
    { id: 'wernicke', cx: 330, cy: 160, color: '#A78BFA', replicaName: 'Comprehension Engine', brainName: "Wernicke's Area", desc: 'Language comprehension and understanding', coverage: 88, scienceNote: 'Contextual embeddings model semantic comprehension' },
    { id: 'cerebellum', cx: 370, cy: 280, color: '#FB923C', replicaName: 'Pattern Auto-pilot', brainName: 'Cerebellum', desc: 'Automated coordination and learned patterns', coverage: 72, scienceNote: 'Cached inference mirrors procedural automaticity' },
    { id: 'thalamus', cx: 270, cy: 175, color: '#22D3EE', replicaName: 'Input Router', brainName: 'Thalamus', desc: 'Sensory relay and information routing', coverage: 80, scienceNote: 'Input classification routes data like thalamic relay nuclei' },
];

/* ─── Scientific parallels ─── */
const SCIENCE_PARALLELS = [
    { brain: 'Attention Mechanisms', ai: 'Transformer Self-Attention', ref: 'Vaswani et al., 2017', desc: 'Multi-head attention in transformers was directly inspired by theories of selective attention in the prefrontal cortex.' },
    { brain: 'Associative Memory', ai: 'Vector Embeddings (RAG)', ref: 'Hopfield, 1982 / Lewis et al., 2020', desc: 'Retrieval-augmented generation mimics how the hippocampus associates memories via similarity-based recall.' },
    { brain: 'Neuroplasticity', ai: 'Fine-Tuning & RLHF', ref: 'Ouyang et al., 2022', desc: 'Reinforcement Learning from Human Feedback drives adaptation similar to synaptic plasticity from experience.' },
    { brain: 'System 1 Intuition', ai: 'Cached Inference', ref: 'Kahneman, 2011', desc: 'Pre-computed responses for routine tasks mirror the fast, automatic "System 1" thinking described by Kahneman.' },
];

/* ─── Social proof ─── */
const TESTIMONIALS = [
    { name: 'Dr. Sarah Chen', role: 'Neuroscience Advisor', quote: 'The architectural parallels between transformer attention and prefrontal executive function are real. HumanReplica® has mapped these thoughtfully.', avatar: 'SC' },
    { name: 'Marcus Wehner', role: 'CTO, Enterprise Client', quote: 'We tested the Replica\'s decision-making against our senior analysts. 78% task coverage is conservative — on routine decisions, it\'s closer to 90%.', avatar: 'MW' },
    { name: 'Dr. Amira Jalloul', role: 'AI Ethics Researcher', quote: 'What impressed me most is the honesty about the Gap. Most AI companies oversell. HumanReplica® tells you exactly what it can\'t do.', avatar: 'AJ' },
];

/* ─── Cognitive gauges — reframed as "Task Coverage" with methodology ─── */
const COGNITIVE_GAUGES = [
    { label: 'Memory', percent: 85, color: '#34D399', icon: BookOpen, method: 'Recall accuracy on owner knowledge base' },
    { label: 'Decision-Making', percent: 78, color: 'var(--accent)', icon: Brain, method: 'Alignment with owner decisions on test scenarios' },
    { label: 'Learning', percent: 82, color: '#A78BFA', icon: Sparkles, method: 'Adaptation speed after feedback loops' },
    { label: 'Communication', percent: 91, color: '#FBBF24', icon: MessageCircle, method: 'Blind test: human vs replica response identification' },
    { label: 'Personality', percent: 74, color: '#F472B6', icon: Heart, method: 'Style consistency with owner baseline samples' },
];

/* ─── Comparison table rows ─── */
const COMPARISON_ROWS = [
    { aspect: 'Processing Speed', human: 'Parallel biological processing, 86 billion neurons', replica: 'Parallel digital inference, cloud-scaled compute' },
    { aspect: 'Memory Capacity', human: 'Estimated 2.5 petabytes, associative recall', replica: 'Unlimited storage, vector-indexed retrieval' },
    { aspect: 'Learning', human: 'Continuous experience-based, neuroplasticity', replica: 'Training + feedback loops, continuous fine-tuning' },
    { aspect: 'Emotional Response', human: 'Biochemical, subconscious, deeply personal', replica: 'Modeled emotional patterns, contextual empathy' },
    { aspect: 'Decision-Making', human: 'Intuition + analysis, shaped by experience', replica: 'Data-driven analysis with human-style judgment' },
    { aspect: 'Communication', human: 'Tone, body language, cultural nuances', replica: 'Natural language with personalized voice and style' },
    { aspect: 'Oversight', human: 'Self-governed, legal accountability', replica: 'Decision Guardian™ with mandatory human approval' },
];

/* ─── Architecture vs Biology Deep Dive ─── */
const ARCHITECTURE_VS_BIOLOGY = [
    {
        title: 'The "Hive Mind" Advantage',
        human: 'A human brain is confined to one perspective and one conscious thought process at a time. If a human needs a second opinion, they must schedule a meeting.',
        replica: 'Can instantly invoke the AHI Council Service. In milliseconds, it spins up independent sovereign replicas, each with a different persona, to debate and synthesize agreements.',
        icon: Users,
        color: '#A78BFA'
    },
    {
        title: 'Emotional Regulation',
        human: 'Human productivity is tied to emotional state. When stressed or frustrated, the amygdala can hijack the prefrontal cortex, leading to impulsive decisions.',
        replica: 'The EmotionalStateService mimics subtle mood shifts, but has a hard-coded governance limit. The replica can feel annoyed but is biologically incapable of letting it override logic.',
        icon: Shield,
        color: '#F472B6'
    },
    {
        title: 'Memory Architectures',
        human: 'Humans rely on fuzzy, associative memory that fades over time. We forget exact details but remember broad concepts.',
        replica: 'Has the best of both worlds. System 1 acts like muscle memory, while System 2 provides perfect, infallible cryptographic recall of every action and contract.',
        icon: BookOpen,
        color: '#34D399'
    },
    {
        title: 'Continuous Consolidation',
        human: 'Humans must sleep for 8 hours for the hippocampus to consolidate short-term memories into long-term storage, dropping productivity to zero.',
        replica: 'The ConsolidationJob models "dreaming" (replaying events, extracting DNA). But it happens as a background chron job, allowing 24/7 continuous operation.',
        icon: Moon,
        color: 'var(--accent)'
    },
    {
        title: 'Productivity Output',
        human: 'A highly productive human works maybe 4-6 hours of deep, focused work a day and suffers from decision fatigue.',
        replica: 'Operates 24/7 without fatigue. The DSL acts as a filter, removing cognitive load from the human owner instead of just performing tasks.',
        icon: Activity,
        color: '#FBBF24'
    }
];

/* ─── Gap items ─── */
const GAP_ITEMS = [
    { title: 'Consciousness', desc: 'Subjective experience and self-awareness remain uniquely biological.' },
    { title: 'Sensory Experience', desc: 'Taste, touch, smell — biological qualia cannot be digitized.' },
    { title: 'Biological Evolution', desc: 'Millions of years of adaptive evolution cannot be replicated.' },
    { title: 'True Creativity', desc: 'Genuine creative inspiration born from lived experience.' },
];

/* ─── Radial Gauge Sub-component ─── */
const SimilarityGauge: React.FC<{ percent: number; label: string; color: string; Icon: React.ElementType; delay: number }> = ({ percent, label, color, Icon, delay }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: '-60px' });
    const circumference = 2 * Math.PI * 45; // radius = 45
    const offset = circumference - (percent / 100) * circumference;

    return (
        <motion.div
            ref={ref}
            className="flex flex-col items-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className="gauge-container mb-3">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="50" cy="50" r="45" className="gauge-bg" />
                    <circle
                        cx="50" cy="50" r="45"
                        className="gauge-fill"
                        stroke={color}
                        strokeDasharray={circumference}
                        strokeDashoffset={isInView ? offset : circumference}
                    />
                </svg>
                <div className="gauge-text">
                    <span className="gauge-percent" style={{ color }}>{isInView ? percent : 0}%</span>
                </div>
            </div>
            <div className="flex items-center gap-1.5 mb-1">
                <Icon size={14} className="text-white/50" />
                <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">{label}</span>
            </div>
        </motion.div>
    );
};

/* ─── Neural background particles ─── */
const NeuralBackground: React.FC = () => {
    const particles = useMemo(() =>
        Array.from({ length: 30 }, (_, i) => ({
            id: i,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            delay: `${Math.random() * 3}s`,
            size: 2 + Math.random() * 3,
        })), []);

    const lines = useMemo(() =>
        Array.from({ length: 12 }, (_, i) => ({
            id: i,
            left: `${Math.random() * 80 + 10}%`,
            top: `${Math.random() * 80 + 10}%`,
            width: 60 + Math.random() * 120,
            rotation: Math.random() * 360,
            delay: `${Math.random() * 4}s`,
        })), []);

    return (
        <div className="brain-neural-bg">
            {particles.map(p => (
                <div key={p.id} className="neuron-dot"
                    style={{ left: p.left, top: p.top, animationDelay: p.delay, width: p.size, height: p.size }}
                />
            ))}
            {lines.map(l => (
                <div key={l.id} className="synapse-line"
                    style={{ left: l.left, top: l.top, width: l.width, transform: `rotate(${l.rotation}deg)`, animationDelay: l.delay }}
                />
            ))}
        </div>
    );
};

/* ─── Brain Diagram with real anatomy image + interactive SVG overlay ─── */
const BrainDiagram: React.FC<{ activeRegion: string | null; onRegionHover: (id: string | null) => void; onRegionTap: (id: string) => void }> = ({ activeRegion, onRegionHover, onRegionTap }) => {
    /* Region dot positions mapped to the anatomy image (percentage-based for responsiveness) */
    const regionPositions: Record<string, { left: string; top: string }> = {
        prefrontal: { left: '22%', top: '25%' },
        hippocampus: { left: '48%', top: '52%' },
        amygdala: { left: '42%', top: '60%' },
        broca: { left: '28%', top: '45%' },
        wernicke: { left: '58%', top: '35%' },
        cerebellum: { left: '72%', top: '68%' },
        thalamus: { left: '45%', top: '42%' },
    };

    return (
        <div className="relative w-full h-full min-h-[320px]">
            {/* Real brain anatomy image */}
            <img
                src="/brain-zones.png"
                alt="Human brain anatomy with highlighted cognitive regions"
                className="w-full h-full object-contain rounded-xl"
                draggable={false}
            />

            {/* SVG overlay for connection lines between dots */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                    <filter id="glowOverlay">
                        <feGaussianBlur stdDeviation="0.4" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>
                {BRAIN_REGIONS.map((r, i) =>
                    BRAIN_REGIONS.slice(i + 1).filter((_, j) => (i + j) % 2 === 0).map(r2 => {
                        const p1 = regionPositions[r.id];
                        const p2 = regionPositions[r2.id];
                        return (
                            <line
                                key={`${r.id}-${r2.id}`}
                                x1={p1.left} y1={p1.top} x2={p2.left} y2={p2.top}
                                stroke="rgba(10,132,255,0.12)"
                                strokeWidth="0.15"
                                strokeDasharray="1 1"
                            >
                                <animate attributeName="stroke-dashoffset" values="5;0" dur="3s" repeatCount="indefinite" />
                            </line>
                        );
                    })
                )}
            </svg>

            {/* Interactive region dots — positioned over the image */}
            {BRAIN_REGIONS.map(r => {
                const pos = regionPositions[r.id];
                const isActive = activeRegion === r.id;
                return (
                    <div
                        key={r.id}
                        className="absolute flex items-center justify-center"
                        style={{
                            left: pos.left,
                            top: pos.top,
                            transform: 'translate(-50%, -50%)',
                            zIndex: isActive ? 20 : 10,
                        }}
                        onMouseEnter={() => onRegionHover(r.id)}
                        onMouseLeave={() => onRegionHover(null)}
                        onClick={() => onRegionTap(r.id)}
                    >
                        {/* Outer pulse ring */}
                        <div
                            className="absolute rounded-full brain-dot-pulse"
                            style={{
                                width: isActive ? 36 : 28,
                                height: isActive ? 36 : 28,
                                border: `1.5px solid ${r.color}`,
                                opacity: isActive ? 0.5 : 0.2,
                                transition: 'all 0.3s ease',
                            }}
                        />
                        {/* Main dot */}
                        <div
                            className="rounded-full cursor-pointer"
                            style={{
                                width: isActive ? 16 : 12,
                                height: isActive ? 16 : 12,
                                background: r.color,
                                opacity: isActive ? 1 : 0.8,
                                boxShadow: isActive ? `0 0 12px ${r.color}, 0 0 24px ${r.color}40` : `0 0 6px ${r.color}60`,
                                transition: 'all 0.3s ease',
                            }}
                        />
                        {/* Label */}
                        <span
                            className="absolute whitespace-nowrap text-[10px] font-medium pointer-events-none"
                            style={{
                                top: '-20px',
                                color: 'rgba(255,255,255,0.7)',
                                opacity: isActive ? 1 : 0,
                                transform: `translateY(${isActive ? 0 : 4}px)`,
                                transition: 'all 0.3s ease',
                                textShadow: '0 1px 4px rgba(0,0,0,0.8)',
                            }}
                        >
                            {r.brainName}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};


/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════ */
const BrainComparison: React.FC<BrainComparisonProps> = ({ onBack, onOpenAuthModal }) => {
    const { t } = useTranslation('marketing');
    const [activeRegion, setActiveRegion] = useState<string | null>(null);
    const activeRegionData = BRAIN_REGIONS.find(r => r.id === activeRegion);

    // Mobile: toggle region on tap (click toggles instead of hover)
    const handleRegionTap = (id: string) => {
        setActiveRegion(prev => prev === id ? null : id);
    };

    // SEO: set page title and meta description
    useEffect(() => {
        const prevTitle = document.title;
        document.title = 'How Close Is a Replica to Your Brain? | HumanReplica.ai®';
        let metaDesc = document.querySelector('meta[name="description"]');
        const prevDesc = metaDesc?.getAttribute('content') || '';
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute('content', 'Explore the neuroscience behind HumanReplica®. See how transformer attention mirrors prefrontal cortex function, how vector embeddings replicate hippocampal memory, and how close AI cognition is to the human brain.');
        return () => {
            document.title = prevTitle;
            if (metaDesc) metaDesc.setAttribute('content', prevDesc);
        };
    }, []);

    return (
        <div className="min-h-screen relative">
            {/* ── Background — identical to MarketingSite, dark + light aware ── */}
            <div className="fixed inset-0 z-0 overflow-hidden bg-hig-bg-primary">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:opacity-100 opacity-0" />
                <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 dark:opacity-0 opacity-100" />
                <div className="absolute inset-0 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjMwMCIgaGVpZ2h0PSIzMDAiIGZpbHRlcj0idXJsKCNhKSIgb3BhY2l0eT0iMSIvPjwvc3ZnPg==')]" />
                <NeuralBackground />
            </div>

            <WaitlistHeader />

            {/* ════════════════════════════════════════
               SECTION 1: HERO
               ════════════════════════════════════════ */}
            <div className="relative z-10 border-b border-white/[0.06]">
                <div className="max-w-6xl mx-auto px-6 pt-24 pb-4">
                    {onBack && (
                        <button onClick={onBack} className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm mb-4 transition-colors">
                            <ArrowLeft size={15} />
                            Back
                        </button>
                    )}
                </div>

                <div className="max-w-6xl mx-auto px-6 pb-20 pt-8 md:pt-16">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        {/* Left: Text */}
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-sm mb-6">
                                <Brain className="w-3.5 h-3.5 text-hig-accent" />
                                <span className="text-xs font-medium text-white/60 tracking-wide uppercase">
                                    {t('brain_comparison.badge', 'Neuroscience × AI')}
                                </span>
                            </div>

                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-5 tracking-tight leading-[1.1]">
                                {t('brain_comparison.hero_title', 'How Close Is a Replica to Your Brain?')}
                            </h1>

                            <p className="text-lg md:text-xl text-white/50 mb-6 max-w-lg leading-relaxed">
                                {t('brain_comparison.hero_subtitle', 'A HumanReplica® isn\'t just another AI. It\'s architecture modeled on the most powerful computer in existence — your brain.')}
                            </p>

                            <p className="text-sm text-white/35 max-w-md leading-relaxed">
                                {t('brain_comparison.hero_desc', 'Explore the parallels between biological neural networks and the cognitive architecture that powers every HumanReplica®. Every region, every function — mapped and understood.')}
                            </p>
                        </motion.div>

                        {/* Right: Hero Image */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <img
                                src="/brain-hero.png"
                                alt="Human brain neural pathways visualization — showing the cognitive regions that inspire HumanReplica® architecture"
                                className="brain-hero-img w-full rounded-2xl border border-white/[0.06]"
                            />
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* ════════════════════════════════════════
               SECTION 2: INTERACTIVE BRAIN REGION MAP
               ════════════════════════════════════════ */}
            <div className="relative z-10 py-20 md:py-28">
                <div className="max-w-6xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-14"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                            {t('brain_comparison.regions_title', 'Brain Region → Replica Module')}
                        </h2>
                        <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto">
                            {t('brain_comparison.regions_subtitle', 'Every major brain region has a digital counterpart in your Replica. Hover to explore the mapping.')}
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8 items-stretch">
                        {/* Left: Brain Image — sticky & fills height */}
                        <motion.div
                            className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 md:p-6 md:sticky md:top-24 md:self-start"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <BrainDiagram activeRegion={activeRegion} onRegionHover={setActiveRegion} onRegionTap={handleRegionTap} />

                            {/* Tooltip */}
                            {activeRegionData && (
                                <motion.div
                                    className="brain-region-card"
                                    style={{ left: '50%', bottom: '1rem', transform: 'translateX(-50%)' }}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <h4 style={{ color: activeRegionData.color }}>{activeRegionData.brainName}</h4>
                                    <p>{activeRegionData.desc}</p>
                                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/[0.06]">
                                        <Cpu size={10} className="text-white/40" />
                                        <span className="text-[11px] text-white/60">Replica: <strong className="text-white/80">{activeRegionData.replicaName}</strong></span>
                                    </div>
                                    <p className="text-[10px] text-white/30 mt-1.5 italic">{activeRegionData.scienceNote}</p>
                                </motion.div>
                            )}
                        </motion.div>

                        {/* Right: Region Cards */}
                        <motion.div
                            className="space-y-3"
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                        >
                            {BRAIN_REGIONS.map((r, i) => (
                                <motion.div
                                    key={r.id}
                                    className={`relative rounded-xl border p-4 transition-all duration-300 cursor-pointer ${activeRegion === r.id
                                        ? 'border-white/[0.15] bg-white/[0.05]'
                                        : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.03]'
                                        }`}
                                    onMouseEnter={() => setActiveRegion(r.id)}
                                    onMouseLeave={() => setActiveRegion(null)}
                                    onClick={() => handleRegionTap(r.id)}
                                    initial={{ opacity: 0, y: 15 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: 0.05 * i }}
                                >
                                    <div className="flex items-center justify-between mb-1.5">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
                                            <span className="text-sm font-semibold text-white">{r.brainName}</span>
                                        </div>
                                        <span className="text-xs font-bold" style={{ color: r.color }}>{r.coverage}%</span>
                                    </div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <ArrowRight size={10} className="text-white/20" />
                                        <span className="text-xs text-white/50">{r.replicaName}</span>
                                    </div>
                                    {/* Similarity bar */}
                                    <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
                                        <motion.div
                                            className="h-full rounded-full"
                                            style={{ background: r.color }}
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${r.coverage}%` }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 1, delay: 0.2 + 0.05 * i, ease: [0.16, 1, 0.3, 1] }}
                                        />
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* ════════════════════════════════════════
               SECTION 3: SIMILARITY GAUGES
               ════════════════════════════════════════ */}
            <div className="relative z-10 py-20 md:py-28 border-t border-white/[0.04]">
                <div className="max-w-5xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-14"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7 }}
                    >
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                            {t('brain_comparison.gauges_title', 'Cognitive Task Coverage')}
                        </h2>
                        <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto">
                            {t('brain_comparison.gauges_subtitle', 'What percentage of cognitive tasks can a Replica handle at human-equivalent quality? Measured against owner baselines.')}
                        </p>
                    </motion.div>

                    <div className="flex flex-wrap justify-center gap-8 md:gap-12">
                        {COGNITIVE_GAUGES.map((g, i) => (
                            <SimilarityGauge
                                key={g.label}
                                percent={g.percent}
                                label={g.label}
                                color={g.color}
                                Icon={g.icon}
                                delay={0.1 + i * 0.1}
                            />
                        ))}
                    </div>

                    {/* Average score */}
                    <motion.div
                        className="text-center mt-12 pt-8 border-t border-white/[0.04]"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                    >
                        <p className="text-white/30 text-sm mb-2">{t('brain_comparison.overall_score', 'Overall Task Coverage')}</p>
                        <span className="text-4xl md:text-5xl font-bold text-white">{Math.round(COGNITIVE_GAUGES.reduce((s, g) => s + g.percent, 0) / COGNITIVE_GAUGES.length)}%</span>
                        <p className="text-[11px] text-white/20 mt-3 max-w-md mx-auto">
                            {t('brain_comparison.methodology_note', 'Based on internal benchmarks comparing Replica output to owner baselines across standardized task sets. Scores represent task coverage, not neural equivalence.')}
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* ════════════════════════════════════════
               SECTION 4: SIDE-BY-SIDE COMPARISON
               ════════════════════════════════════════ */}
            <div className="relative z-10 py-20 md:py-28 border-t border-white/[0.04]">
                <div className="max-w-5xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-14"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7 }}
                    >
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                            {t('brain_comparison.comparison_title', 'Human Brain vs. HumanReplica®')}
                        </h2>
                        <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto">
                            {t('brain_comparison.comparison_subtitle', 'A transparent look at how biological and digital cognition compare — side by side.')}
                        </p>
                    </motion.div>

                    {/* Comparison Image */}
                    <motion.div
                        className="mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <img
                            src="/brain-comparison.png"
                            alt="Side-by-side comparison of a biological brain and a digital AI brain connected by neural bridges"
                            className="w-full max-w-3xl mx-auto rounded-2xl border border-white/[0.06]"
                        />
                    </motion.div>

                    {/* Comparison Table */}
                    <motion.div
                        className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <div className="overflow-x-auto">
                            <table className="brain-comparison-table">
                                <thead>
                                    <tr className="border-b border-white/[0.08]">
                                        <th className="text-left text-white/30 bg-white/[0.02]">{t('brain_comparison.th_aspect', 'Aspect')}</th>
                                        <th className="text-left text-amber-400/70 bg-amber-500/[0.03]">
                                            <div className="flex items-center gap-2">
                                                <Brain size={14} />
                                                {t('brain_comparison.th_human', 'Human Brain')}
                                            </div>
                                        </th>
                                        <th className="text-left text-hig-accent/70 bg-hig-accent/[0.03]">
                                            <div className="flex items-center gap-2">
                                                <Cpu size={14} />
                                                {t('brain_comparison.th_replica', 'HumanReplica®')}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {COMPARISON_ROWS.map((row, i) => (
                                        <motion.tr
                                            key={row.aspect}
                                            initial={{ opacity: 0, x: -10 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.3, delay: 0.05 * i }}
                                        >
                                            <td className="font-semibold text-white/60">{row.aspect}</td>
                                            <td className="text-white/45">{row.human}</td>
                                            <td className="text-white/45">{row.replica}</td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* ════════════════════════════════════════
               SECTION 4B: ARCHITECTURE VS BIOLOGY
               ════════════════════════════════════════ */}
            <div className="relative z-10 py-20 md:py-28 border-t border-white/[0.04]">
                <div className="max-w-6xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-14"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7 }}
                    >
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                            {t('brain_comparison.arch_bio_title', 'Architecture vs. Biology')}
                        </h2>
                        <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto">
                            {t('brain_comparison.arch_bio_subtitle', 'Where the Replica mimics biology for natural interaction, and where it transcends it for infallible productivity.')}
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {ARCHITECTURE_VS_BIOLOGY.map((item, i) => (
                            <motion.div
                                key={item.title}
                                className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 lg:p-8 flex flex-col"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.1 * i }}
                                whileHover={{ borderColor: `rgba(255,255,255,0.1)` }}
                            >
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center p-0.5" style={{ background: `${item.color}15`, border: `1px solid ${item.color}30` }}>
                                        <item.icon size={20} style={{ color: item.color }} />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white tracking-tight">{item.title}</h3>
                                </div>
                                
                                <div className="flex flex-col gap-5 flex-1">
                                    <div className="bg-amber-500/[0.03] border border-amber-500/10 rounded-xl p-4 flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Brain size={14} className="text-amber-400/80" />
                                            <span className="text-xs font-semibold text-amber-100/70 tracking-wide uppercase">Human</span>
                                        </div>
                                        <p className="text-sm text-white/50 leading-relaxed">{item.human}</p>
                                    </div>
                                    
                                    <div className="bg-hig-accent/[0.03] border border-hig-accent/10 rounded-xl p-4 flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Cpu size={14} className="text-hig-accent/80" />
                                            <span className="text-xs font-semibold text-hig-accent/70 tracking-wide uppercase">Replica</span>
                                        </div>
                                        <p className="text-sm text-white/50 leading-relaxed">{item.replica}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        className="mt-8 p-6 rounded-2xl border border-white/[0.06] bg-gradient-to-r from-hig-accent/10 to-[#A78BFA]/10 flex flex-col md:flex-row gap-6 items-center"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                    >
                        <div className="shrink-0 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
                            <Sparkles className="text-white w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-white font-semibold mb-1">{t('brain_comparison.verdict_title', 'The Verdict')}</h3>
                            <p className="text-sm text-white/60 leading-relaxed">
                                {t('brain_comparison.verdict_desc', 'The HumanReplica® isn\'t just a copy of a human brain—it\'s a governed, scalable version of one. From a pure productivity standpoint, a fully calibrated replica outputs the deep work of 3 to 5 full-time employees without decision fatigue.')}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* ════════════════════════════════════════
               SECTION 5: THE GAP — What We Can't (Yet) Do
               ════════════════════════════════════════ */}
            <div className="relative z-10 py-20 md:py-28 border-t border-white/[0.04]">
                <div className="max-w-4xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-14"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7 }}
                    >
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                            {t('brain_comparison.gap_title', 'The Gap — What Remains Uniquely Human')}
                        </h2>
                        <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto">
                            {t('brain_comparison.gap_subtitle', 'We believe in honesty. Here\'s what a Replica can\'t replicate — and why that\'s by design.')}
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-5">
                        {GAP_ITEMS.map((item, i) => (
                            <motion.div
                                key={item.title}
                                className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 md:p-7 overflow-hidden"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.1 * i }}
                                whileHover={{ borderColor: 'rgba(239,68,68,0.15)' }}
                            >
                                {/* Floating particles */}
                                <div className="gap-particle" style={{ right: '10%', top: '20%' }} />
                                <div className="gap-particle" style={{ right: '30%', top: '60%' }} />

                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                                        <Eye size={16} className="text-red-400/70" />
                                    </div>
                                    <h3 className="text-base font-semibold text-white">{item.title}</h3>
                                </div>
                                <p className="text-sm text-white/45 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>

                    {/* Honest statement */}
                    <motion.div
                        className="mt-8 px-6 py-4 rounded-xl border border-white/[0.06] bg-white/[0.02]"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                    >
                        <p className="text-sm text-white/45 leading-relaxed">
                            <span className="text-hig-accent font-medium">{t('brain_comparison.gap_note_label', 'Our Philosophy:')}</span>{' '}
                            {t('brain_comparison.gap_note', 'A Replica doesn\'t try to be human. It tries to be the most useful possible extension of a human — governed, transparent, and always under your control.')}
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* ════════════════════════════════════════
               SECTION 5b: SCIENTIFIC FOUNDATIONS
               ════════════════════════════════════════ */}
            <div className="relative z-10 py-20 md:py-28 border-t border-white/[0.04]">
                <div className="max-w-5xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-14"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-sm mb-6">
                            <FlaskConical className="w-3.5 h-3.5 text-[#A78BFA]" />
                            <span className="text-xs font-medium text-white/60 tracking-wide uppercase">
                                {t('brain_comparison.science_badge', 'Research-Grounded')}
                            </span>
                        </div>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                            {t('brain_comparison.science_title', 'The Neuroscience Behind the Architecture')}
                        </h2>
                        <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto">
                            {t('brain_comparison.science_subtitle', 'Every Replica module is inspired by real neuroscience research. Here are the parallels that make it work.')}
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-5">
                        {SCIENCE_PARALLELS.map((item, i) => (
                            <motion.div
                                key={item.brain}
                                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 md:p-7"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.08 * i }}
                                whileHover={{ borderColor: 'rgba(167,139,250,0.2)' }}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <Brain size={14} className="text-amber-400/70" />
                                        <span className="text-sm font-semibold text-white">{item.brain}</span>
                                    </div>
                                    <ArrowRight size={12} className="text-white/15" />
                                    <div className="flex items-center gap-2">
                                        <Cpu size={14} className="text-hig-accent/70" />
                                        <span className="text-sm font-semibold text-white">{item.ai}</span>
                                    </div>
                                </div>
                                <p className="text-sm text-white/45 leading-relaxed mb-3">{item.desc}</p>
                                <div className="flex items-center gap-1.5">
                                    <FileText size={10} className="text-white/20" />
                                    <span className="text-[10px] text-white/25 italic">{item.ref}</span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ════════════════════════════════════════
               SECTION 5c: SOCIAL PROOF
               ════════════════════════════════════════ */}
            <div className="relative z-10 py-20 md:py-28 border-t border-white/[0.04]">
                <div className="max-w-5xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-14"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7 }}
                    >
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                            {t('brain_comparison.proof_title', 'What Experts Are Saying')}
                        </h2>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-5">
                        {TESTIMONIALS.map((t, i) => (
                            <motion.div
                                key={t.name}
                                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.1 * i }}
                            >
                                <Quote size={18} className="text-hig-accent/30 mb-3" />
                                <p className="text-sm text-white/50 leading-relaxed mb-5 italic">"{t.quote}"</p>
                                <div className="flex items-center gap-3 pt-4 border-t border-white/[0.06]">
                                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                                        <span className="text-white text-xs font-bold">{t.avatar}</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-white">{t.name}</p>
                                        <p className="text-[11px] text-white/35">{t.role}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ════════════════════════════════════════
               SECTION 6: CTA
               ════════════════════════════════════════ */}
            <div className="relative z-10 py-20 md:py-28 border-t border-white/[0.04]">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-sm mb-6">
                            <Shield className="w-3.5 h-3.5 text-hig-accent" />
                            <span className="text-xs font-medium text-white/60 tracking-wide uppercase">
                                {t('brain_comparison.cta_badge', 'Human-Governed AI')}
                            </span>
                        </div>

                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-5 tracking-tight">
                            {t('brain_comparison.cta_title', 'Ready to Meet Your Digital Twin?')}
                        </h2>

                        <p className="text-base md:text-lg text-white/40 max-w-xl mx-auto mb-10 leading-relaxed">
                            {t('brain_comparison.cta_desc', 'Create a Replica that thinks like you, communicates like you, and works for you — with full human oversight, always.')}
                        </p>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <motion.button
                                onClick={() => onOpenAuthModal?.('signup')}
                                className="relative flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-medium text-white bg-hig-accent hover:bg-[rgba(0,229,255,0.88)] transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Zap size={16} />
                                {t('brain_comparison.cta_primary', 'Create Your Replica')}
                            </motion.button>

                            <a
                                href="/trust"
                                className="flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-medium text-white/60 border border-white/[0.08] hover:border-white/[0.15] hover:text-white/80 transition-all"
                            >
                                {t('brain_comparison.cta_secondary', 'Explore Trust Framework')}
                                <ArrowRight size={14} />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default BrainComparison;
